/**
 * Floating Settings Button Component
 *
 * A wrench icon button in the bottom right corner that opens
 * a settings panel for noise control configuration.
 *
 * Noise Control Options:
 * - None: No noise reduction (OpenAI value: 'none')
 * - Headset: Near-field noise reduction for close microphones (OpenAI value: 'near_field')
 * - Public: Far-field noise reduction for laptop/room mics (OpenAI value: 'far_field') - DEFAULT
 */

import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { getUserPreferences, updateUserPreferences } from '@/services/api';
import type { NoiseReductionType } from '@/types/user';

// Noise option type - labels and descriptions are translated in component
type NoiseOption = { labelKey: string; value: NoiseReductionType; descKey: string };
const NOISE_OPTIONS: NoiseOption[] = [
  { labelKey: 'settings.options.none', value: 'none', descKey: 'settings.options.noneDesc' },
  { labelKey: 'settings.options.headset', value: 'near_field', descKey: 'settings.options.headsetDesc' },
  { labelKey: 'settings.options.public', value: 'far_field', descKey: 'settings.options.publicDesc' },
];

interface SettingsButtonProps {
  onNoiseSettingChange?: (value: NoiseReductionType) => void;
  disabled?: boolean; // Disable when voice session is active
}

// Detect audio device type from label to recommend noise reduction mode
const detectDeviceType = (deviceLabel: string): 'near_field' | 'far_field' | null => {
  const label = deviceLabel.toLowerCase();

  // Patterns indicating headset/earbuds (near_field recommended)
  const headsetPatterns = [
    'headset', 'headphone', 'earphone', 'earbud', 'airpod', 'buds',
    'jabra', 'plantronics', 'logitech headset', 'hyperx', 'steelseries',
    'razer', 'corsair', 'astro', 'turtle beach', 'sennheiser',
    'bose', 'sony wh-', 'sony wf-', 'beats', 'jbl', 'samsung buds',
    'pixel buds', 'galaxy buds', 'bluetooth'
  ];

  // Patterns indicating built-in/laptop mic (far_field recommended)
  const builtInPatterns = [
    'built-in', 'internal', 'laptop', 'macbook', 'webcam',
    'integrated', 'realtek', 'conexant', 'default', 'array',
    'usb audio', 'displayport', 'hdmi'
  ];

  for (const pattern of headsetPatterns) {
    if (label.includes(pattern)) return 'near_field';
  }

  for (const pattern of builtInPatterns) {
    if (label.includes(pattern)) return 'far_field';
  }

  return null; // Unknown device type
};

export default function SettingsButton({ onNoiseSettingChange, disabled = false }: SettingsButtonProps) {
  const { t } = useTranslation('voice');
  const [isOpen, setIsOpen] = useState(false);
  const [noiseReduction, setNoiseReduction] = useState<NoiseReductionType>('far_field');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [recommendedMode, setRecommendedMode] = useState<NoiseReductionType | null>(null);
  const [detectedDevice, setDetectedDevice] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Load user preferences on mount
  useEffect(() => {
    const loadPreferences = async () => {
      setIsLoading(true);
      try {
        const prefs = await getUserPreferences();
        setNoiseReduction(prefs.noise_reduction);
      } catch (error) {
        console.error('Failed to load preferences:', error);
        // Keep default value on error
      } finally {
        setIsLoading(false);
      }
    };
    loadPreferences();
  }, []);

  // Detect audio input device to recommend noise reduction mode
  useEffect(() => {
    const detectAudioDevice = async () => {
      try {
        // Need to request permission first to get device labels
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

        // Get all audio input devices
        const devices = await navigator.mediaDevices.enumerateDevices();
        const audioInputs = devices.filter(d => d.kind === 'audioinput');

        // Stop the stream immediately (we just needed permission for labels)
        stream.getTracks().forEach(track => track.stop());

        // Find the default or first device with a label
        const defaultDevice = audioInputs.find(d => d.deviceId === 'default');
        const deviceWithLabel = audioInputs.find(d => d.label);
        const device = defaultDevice || deviceWithLabel;

        if (device && device.label) {
          console.log('🎤 [DEVICE DETECTION] Audio device:', device.label);
          setDetectedDevice(device.label);

          const detected = detectDeviceType(device.label);
          if (detected) {
            console.log('🎤 [DEVICE DETECTION] Recommended mode:', detected);
            setRecommendedMode(detected);
          }
        }
      } catch (error) {
        console.log('🎤 [DEVICE DETECTION] Could not detect device:', error);
        // Permission denied or no devices - just use defaults
      }
    };

    detectAudioDevice();
  }, []);

  // Close panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleNoiseChange = async (value: NoiseReductionType) => {
    setIsSaving(true);
    try {
      await updateUserPreferences(value);
      setNoiseReduction(value);
      onNoiseSettingChange?.(value);
    } catch (error) {
      console.error('Failed to save preference:', error);
      alert(t('errors.settingSaveFailed'));
    } finally {
      setIsSaving(false);
    }
  };

  // Close panel when disabled (session started)
  useEffect(() => {
    if (disabled && isOpen) {
      setIsOpen(false);
    }
  }, [disabled, isOpen]);

  return (
    <div className="fixed bottom-6 right-6 z-50" ref={panelRef}>
      {/* Settings Panel */}
      {isOpen && !disabled && (
        <div className="absolute bottom-14 right-0 w-72 bg-white/95 backdrop-blur-md rounded-xl shadow-2xl border border-gray-200 overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-200">
          {/* Header */}
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
            <h3 className="text-sm font-semibold text-gray-900">{t('settings.voiceSettings')}</h3>
          </div>

          {/* Content */}
          <div className="p-4">
            {/* Noise Control Section */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-2">
                {t('settings.noiseControl')}
              </label>
              <p className="text-xs text-gray-500 mb-3">
                {t('settings.filtersBackground')}
              </p>

              {/* Detected Device Info */}
              {detectedDevice && (
                <div className="mb-3 px-2 py-1.5 bg-gray-50 rounded-md">
                  <p className="text-xs text-gray-600 flex items-center gap-1.5">
                    <span className="text-gray-400">🎤</span>
                    <span className="truncate">{detectedDevice}</span>
                  </p>
                </div>
              )}

              {isLoading ? (
                <div className="flex items-center justify-center py-4">
                  <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : (
                <div className="space-y-2">
                  {NOISE_OPTIONS.map((option) => {
                    const isRecommended = recommendedMode === option.value && noiseReduction !== option.value;
                    const isSelected = noiseReduction === option.value;

                    return (
                      <button
                        key={option.value}
                        onClick={() => handleNoiseChange(option.value)}
                        disabled={isSaving}
                        className={`relative w-full flex items-center justify-between px-3 py-2.5 rounded-lg border transition-all ${
                          isSelected
                            ? 'bg-blue-50 border-blue-300 text-blue-900'
                            : isRecommended
                              ? 'bg-green-50 border-green-300 text-green-900'
                              : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-gray-300'
                        } ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        {/* Pulsing glow effect for recommended */}
                        {isRecommended && (
                          <div className="absolute inset-0 rounded-lg border-2 border-green-400 animate-pulse opacity-75" />
                        )}

                        <div className="flex flex-col items-start z-10">
                          <span className="text-sm font-medium flex items-center gap-1.5">
                            {t(option.labelKey)}
                            {isRecommended && (
                              <span className="text-[10px] font-normal px-1.5 py-0.5 bg-green-100 text-green-700 rounded-full">
                                {t('settings.recommended')}
                              </span>
                            )}
                          </span>
                          <span className="text-xs text-gray-500">{t(option.descKey)}</span>
                        </div>
                        {isSelected && (
                          <svg
                            className="w-5 h-5 text-blue-600 flex-shrink-0 z-10"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Floating Button */}
      <button
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-all duration-200 ${
          disabled
            ? 'bg-gray-200 text-gray-400 cursor-not-allowed opacity-60'
            : isOpen
              ? 'bg-gray-800 text-white rotate-90'
              : 'bg-white text-gray-700 hover:bg-gray-100 hover:shadow-xl'
        }`}
        title={disabled ? t('settings.settingsUnavailable') : t('settings.voiceSettings')}
      >
        {/* Wrench Icon */}
        <svg
          className="w-5 h-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
          />
        </svg>
      </button>
    </div>
  );
}
